import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { FEATURED_PROFILES } from "config/api-const";
import { PLACEHOLDER_IMAGE } from "constant";
import { getPersonalInfo } from "view/profile/store/selector";
import "./featured-bar.css";

const PLAN_BADGE = {
  Platinum: { label: "Platinum ✦", cls: "fb-badge-platinum" },
  Gold:     { label: "Gold ★",     cls: "fb-badge-gold" },
  Silver:   { label: "Silver ◈",   cls: "fb-badge-silver" },
};

const CACHE_KEY = "featured_profiles_cache";
const CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutes cache

const getCachedProfiles = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { profiles, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp < CACHE_DURATION_MS && Array.isArray(profiles)) {
      return profiles;
    }
  } catch (err) {
    console.warn("[FeaturedBar] Failed to read cache:", err);
  }
  return null;
};

const setCachedProfiles = (profiles) => {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ profiles, timestamp: Date.now() })
    );
  } catch (err) {
    console.warn("[FeaturedBar] Failed to set cache:", err);
  }
};

const FeaturedBar = () => {
  const [rawProfiles, setRawProfiles] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [offset, setOffset]           = useState(0);
  const [isPaused, setIsPaused]       = useState(false);
  const navigate                      = useNavigate();
  const personalInfo                  = useSelector(getPersonalInfo);

  const userIAm = personalInfo?.iAm;

  // 1. Fetch or load from 30-minute cache on mount
  useEffect(() => {
    const cached = getCachedProfiles();
    if (cached) {
      setRawProfiles(cached);
      setLoading(false);
      return;
    }

    axios
      .get(FEATURED_PROFILES)
      .then((res) => {
        if (res.data.success) {
          const valid = (res.data.profiles || []).filter((p) => p.userId);
          setRawProfiles(valid);
          setCachedProfiles(valid);
        }
      })
      .catch((err) => console.error("[FeaturedBar] fetch error:", err))
      .finally(() => setLoading(false));
  }, []);

  // 2. Filter profiles by logged-in user's gender (Groom sees Brides, Bride sees Grooms)
  const displayProfiles = useMemo(() => {
    if (!rawProfiles.length) return [];
    if (!userIAm || userIAm === "Select") return rawProfiles;

    const isGroom = userIAm === "Groom" || userIAm === "Male";
    const isBride = userIAm === "Bride" || userIAm === "Female";

    if (isGroom) {
      const filtered = rawProfiles.filter(
        (p) => p.iAm === "Bride" || p.iAm === "Female"
      );
      return filtered.length > 0 ? filtered : rawProfiles;
    }

    if (isBride) {
      const filtered = rawProfiles.filter(
        (p) => p.iAm === "Groom" || p.iAm === "Male"
      );
      return filtered.length > 0 ? filtered : rawProfiles;
    }

    return rawProfiles;
  }, [rawProfiles, userIAm]);

  // Reset offset if displayProfiles length changes
  useEffect(() => {
    setOffset(0);
  }, [displayProfiles.length]);

  // Auto-scroll every 4 s
  const advance = useCallback(() => {
    if (!displayProfiles.length) return;
    setOffset((prev) => (prev + 1) % displayProfiles.length);
  }, [displayProfiles.length]);

  useEffect(() => {
    if (isPaused || displayProfiles.length <= 1) return;
    const id = setInterval(advance, 4000);
    return () => clearInterval(id);
  }, [isPaused, advance, displayProfiles.length]);

  if (loading || displayProfiles.length === 0) return null;

  const profile = displayProfiles[offset % displayProfiles.length] || displayProfiles[0];
  const badge   = PLAN_BADGE[profile.plan] || PLAN_BADGE.Silver;

  // Build info rows matching the reference image
  const horoscopeRow = [profile.gotra, profile.rasi, profile.nakshatra]
    .filter(Boolean)
    .join(" | ");

  const locationRow = [profile.city, profile.state]
    .filter(Boolean)
    .join(", ");

  return (
    <aside
      className="fb-root"
      aria-label="Featured profiles"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Header */}
      <div className="fb-header">
        <span className="fb-header-icon">⭐</span>
        <span className="fb-header-title">FEATURED PROFILES</span>
        <span className="fb-header-icon">⭐</span>
        {/* <span className="fb-header-sub">Silver · Gold · Platinum members</span> */}
      </div>

      {/* Card row */}
      <div className="fb-card-row">
        {/* Prev button */}
        <button
          className="fb-nav fb-nav-prev"
          aria-label="Previous featured profile"
          onClick={() =>
            setOffset((p) => (p === 0 ? displayProfiles.length - 1 : p - 1))
          }
        >
          &#8250;
        </button>

        {/* Horizontal card */}
        <div
          className={`fb-card ${badge.cls}`}
          onClick={() => navigate(`/members/${profile.userId}`)}
          onKeyDown={(e) =>
            e.key === "Enter" && navigate(`/members/${profile.userId}`)
          }
          tabIndex={0}
          role="button"
          aria-label={`View ${profile.name}'s profile`}
        >
          {/* Avatar */}
          <div className="fb-avatar-wrap">
            <img
              src={profile.editedUrl || profile.profileImage || PLACEHOLDER_IMAGE[100]}
              alt={profile.name}
              className="fb-avatar"
              loading="lazy"
            />
          </div>

          {/* Details */}
          <div className="fb-details">
            {/* Name + ID + degree + age */}
            <p className="fb-name">
              {profile.name}
              {profile.userId && (
                <span className="fb-id"> (ID: {profile.userId})</span>
              )}
              {profile.degree && (
                <span className="fb-degree"> {profile.degree}</span>
              )}
              {profile.age && (
                <span className="fb-age"> | Age: {profile.age}</span>
              )}
            </p>

            {/* Gotra | Rasi | Nakshatra */}
            {horoscopeRow && (
              <p className="fb-meta">
                {profile.gotra && <><strong>Gotra:</strong> {profile.gotra} | </>}
                {profile.rasi && <><strong>Rasi:</strong> {profile.rasi} | </>}
                {profile.nakshatra && <><strong>Nakshatra:</strong> {profile.nakshatra}</>}
              </p>
            )}

            {/* Occupation */}
            {profile.occupation && (
              <p className="fb-meta">
                <strong>Occupation:</strong> {profile.occupation}
              </p>
            )}

            {/* HomeTown */}
            {locationRow && (
              <p className="fb-meta">
                <strong>HomeTown:</strong> {locationRow}
              </p>
            )}
          </div>

          {/* Plan badge */}
          <span className={`fb-badge ${badge.cls}`}>{badge.label}</span>
        </div>

        {/* Next button */}
        <button
          className="fb-nav fb-nav-next"
          aria-label="Next featured profile"
          onClick={() =>
            setOffset((p) => (p + 1) % displayProfiles.length)
          }
        >
          &#8250;
        </button>
      </div>

      {/* Dots */}
      {displayProfiles.length > 1 && (
        <div className="fb-dots" role="tablist" aria-label="Featured profile navigation">
          {displayProfiles.map((_, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={i === offset}
              aria-label={`Go to profile ${i + 1}`}
              className={`fb-dot${i === offset ? " fb-dot-active" : ""}`}
              onClick={() => setOffset(i)}
            />
          ))}
        </div>
      )}
    </aside>
  );
};

export default FeaturedBar;
